import React, { useRef } from 'react'
import ReactDOM from 'react-dom'
import QRCode from 'react-qr-code'
import { Button, Panel } from 'rsuite'

import { VehicleBox } from '@@partner:types:app'
import { GQL_ID } from '@@partner:types:graphql'
import { generateBoxQRData, qrDataToString, getConditionLabel } from '@@partner:helpers/qrCode'

import './BoxLabelPrinter.scss'

interface BoxLabelProps {
  box: VehicleBox
  vehicleId: string
}

// Компонент одной этикетки
function BoxLabel ({ box, vehicleId }: BoxLabelProps) {
  const qrData = generateBoxQRData(box, vehicleId.toString())
  const qrString = qrDataToString(qrData)
  
  const parsed = parseBoxNumber(box.number)
  const boxId = `${parsed.rowLetter}${parsed.column.toString().padStart(2, '0')}${parsed.place.toString().padStart(2, '0')}`
  const conditionLabel = getConditionLabel(qrData.c)

  return (
    <div className="BoxLabel">
      <div className="BoxLabel__Content">
        <div className="BoxLabel__QR">
          <QRCode
            value={qrString}
            size={120}
            level="M"
          />
        </div>
        <div className="BoxLabel__Info">
          <div className="BoxLabel__Id">{boxId}</div>
          <div className="BoxLabel__Number">№{box.number}</div>
          <div className="BoxLabel__Condition">{conditionLabel}</div>
        </div>
      </div>
    </div>
  )
}

interface BoxLabelPrinterProps {
  boxes: VehicleBox[]
  vehicleId: GQL_ID
}

export default function BoxLabelPrinter ({ boxes, vehicleId }: BoxLabelPrinterProps) {
  const handlePrintRow = (rowBoxes: VehicleBox[], rowLetter: string) => {
    // Создаем временный контейнер для рендеринга этикеток
    const tempDiv = document.createElement('div')
    tempDiv.style.position = 'absolute'
    tempDiv.style.left = '-9999px'
    document.body.appendChild(tempDiv)

    // Создаем React компоненты
    const sortedBoxes = rowBoxes.sort((a, b) => a.number - b.number)
    
    ReactDOM.render(
      <div>
        <div className="BoxLabelPrinter__Header">
          <h2>Этикетки ряда {rowLetter}</h2>
          <p>Магазин: {vehicleId} | Количество ячеек: {rowBoxes.length}</p>
        </div>
        <div className="BoxLabelPrinter__Grid">
          {sortedBoxes.map(box => (
            <BoxLabel
              key={box.number}
              box={box}
              vehicleId={vehicleId.toString()}
            />
          ))}
        </div>
      </div>,
      tempDiv
    )

    // Ждем рендеринга React компонентов
    setTimeout(() => {
      const printWindow = window.open('', '_blank')
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Этикетки ряда ${rowLetter} - Магазин ${vehicleId}</title>
              <style>
                @page { size: A4; margin: 10mm; }
                body { font-family: Arial, sans-serif; }
                .BoxLabelPrinter__Header { text-align: center; margin-bottom: 20px; }
                .BoxLabelPrinter__Grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; }
                .BoxLabel { border: 2px solid #000; padding: 20px; text-align: center; min-height: 120px; }
                .BoxLabel__Content { display: flex; align-items: center; gap: 20px; }
                .BoxLabel__QR { flex-shrink: 0; }
                .BoxLabel__Info { text-align: left; flex-grow: 1; }
                .BoxLabel__Id { font-weight: bold; font-size: 24px; margin-bottom: 5px; }
                .BoxLabel__Number { font-size: 18px; margin-bottom: 5px; }
                .BoxLabel__Condition { font-size: 14px; color: #666; font-style: italic; }
              </style>
            </head>
            <body>
              ${tempDiv.innerHTML}
            </body>
          </html>
        `)
        printWindow.document.close()
        
        // Ждем загрузки контента перед печатью
        setTimeout(() => {
          printWindow.print()
          printWindow.close()
        }, 1000)
      }
      
      // Очищаем временный элемент
      ReactDOM.unmountComponentAtNode(tempDiv)
      document.body.removeChild(tempDiv)
    }, 100)
  }

  if (boxes.length === 0) {
    return (
      <Panel>
        <p>Нет ячеек для печати этикеток</p>
      </Panel>
    )
  }

  // Группируем ящики по рядам для удобства
  const groupedBoxes = boxes.reduce((acc, box) => {
    const parsed = parseBoxNumber(box.number)
    const rowKey = parsed.rowLetter
    
    if (!acc[rowKey]) {
      acc[rowKey] = []
    }
    acc[rowKey].push(box)
    return acc
  }, {} as Record<string, VehicleBox[]>)

  return (
    <div className="BoxLabelPrinter">
      <div className="BoxLabelPrinter__Header">
        <h3>Печать этикеток ячеек</h3>
        <p>Всего ячеек: {boxes.length}</p>
      </div>

      {Object.keys(groupedBoxes).sort().map(rowLetter => {
        const rowBoxes = groupedBoxes[rowLetter]
        const firstBox = rowBoxes[0]
        const qrData = generateBoxQRData(firstBox, vehicleId.toString())
        const conditionLabel = getConditionLabel(qrData.c)
        
        return (
          <div key={rowLetter} className="BoxLabelPrinter__Row">
            <div className="BoxLabelPrinter__RowHeader">
              <div className="BoxLabelPrinter__RowInfo">
                <h4>Ряд {rowLetter}</h4>
                <p>Ячеек: {rowBoxes.length} | Условия: {conditionLabel}</p>
              </div>
              <Button
                color="green"
                appearance="primary"
                onClick={() => handlePrintRow(rowBoxes, rowLetter)}
              >
                🖨️ Печать этикеток ряда {rowLetter}
              </Button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// Утилитарная функция для парсинга номера ящика
const parseBoxNumber = (number: number): { rowLetter: string, column: number, place: number } => {
  const str = number.toString().padStart(5, '0')
  const rowNumber = parseInt(str[0])
  const column = parseInt(str.slice(1, 3))
  const place = parseInt(str.slice(3, 5))
  
  return {
    rowLetter: String.fromCharCode(65 + rowNumber - 1),
    column,
    place
  }
}
